import React, { useState } from 'react';
import { Modal, Input, Radio } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { cancelOrder } from 'store/actions/data';
import ProductDetailDisplay from 'components/common/ProductDisplay';
import { FONTS } from 'styles/theme/fonts';
import { COLORS } from 'styles/theme/colors';

export const CancelModal = ({ onCancel, modalVisible, product, orderNumber }) => {
  const dispatch = useDispatch();
  const { TextArea } = Input;
  const [reason, setReason] = useState(null);
  const [notes, setNotes] = useState(null);
  const [isCanceled, setIsCanceled] = useState(false);
  const openTab = useSelector((state) => state.openOrders.currentTab);

  const radioStyle = {
    display: 'block',
    color: COLORS.primaryText,
    fontSize: '16px',
    '&:focus': {
      outline: 'none',
    },
  };

  const handleChange = (e) => {
    setReason(e.target.value);
  };

  const handleClick = () => {
    const sellerCancelReason = notes ? `${reason} : ${notes}` : reason;
    dispatch(
      cancelOrder({
        number: orderNumber,
        sellerCanceledReason: sellerCancelReason,
        openTab: openTab,
      }),
    );
    setIsCanceled(true);
  };

  const handleTextAreaChange = (e) => {
    setNotes(e.target.value);
  };

  const handleCancel = () => {
    setReason(null);
    setNotes(null);
    setIsCanceled(false);
    onCancel();
  };

  return (
    <Modal visible={modalVisible} footer={null} title={null} onCancel={handleCancel}>
      <CancelModal.Container isCanceled={isCanceled}>
        <CancelModal.ProductAndReasonContainer isCanceled={isCanceled}>
          {!isCanceled && (
            <>
              <CancelModal.ProductSide>
                <ProductDetailDisplay product={product} isDetailedView />
              </CancelModal.ProductSide>
              <CancelModal.Divider />
            </>
          )}

          <CancelModal.ReasonSide isCanceled={isCanceled}>
            <CancelModal.Title>{isCanceled ? 'canceled sale' : 'cancel sale'}</CancelModal.Title>
            <CancelModal.AreYouSure isCanceled={isCanceled}>
              {isCanceled ? 'You have canceled ' : `Are you sure you would like to cancel `}
              {!isCanceled && <br />}
              {'order #'}
              <CancelModal.OrderNumber>{orderNumber}</CancelModal.OrderNumber>
              {!isCanceled && '?'}
            </CancelModal.AreYouSure>
            {isCanceled && (
              <CancelModal.FindOrder>
                {'You can find this order within the Completed Orders page in the "Canceled" tab.'}
              </CancelModal.FindOrder>
            )}
            <CancelModal.Reason>Reason for cancellation</CancelModal.Reason>
            {!isCanceled && (
              <React.Fragment>
                <CancelModal.RadioChoices>
                  <Radio.Group onChange={handleChange} value={reason}>
                    <Radio style={radioStyle} value="No longer in-stock">
                      No longer in-stock
                    </Radio>
                    <Radio style={radioStyle} value="Damaged/Defected item">
                      Damaged/Defected item
                    </Radio>
                    <Radio style={radioStyle} value="Not able to ship on time">
                      Not able to ship on time
                    </Radio>
                  </Radio.Group>
                  <Radio.Group onChange={handleChange} value={reason}>
                    <Radio style={radioStyle} value="Incorrect Listing">
                      Incorrect Listing
                    </Radio>
                    <Radio style={radioStyle} value="Incorrect Pricing">
                      Incorrect Pricing
                    </Radio>
                    <Radio style={radioStyle} value="Other">
                      Other
                    </Radio>
                  </Radio.Group>
                </CancelModal.RadioChoices>
                <TextArea
                  rows={4}
                  placeholder="Notes"
                  value={notes}
                  onChange={handleTextAreaChange}
                />
                <CancelModal.Disclaimer>
                  DISCLAIMER: Multiple cancellations on orders will lead to a high commission fee.
                </CancelModal.Disclaimer>
              </React.Fragment>
            )}
            {isCanceled && (
              <React.Fragment>
                <CancelModal.Text>{reason}</CancelModal.Text>
                <CancelModal.Reason>Notes</CancelModal.Reason>
                <CancelModal.Text>{notes || 'N/A'}</CancelModal.Text>
              </React.Fragment>
            )}
          </CancelModal.ReasonSide>
        </CancelModal.ProductAndReasonContainer>
        <CancelModal.BackButton isCanceled={isCanceled} onClick={handleCancel}>
          {isCanceled ? 'OKAY' : 'BACK'}
        </CancelModal.BackButton>
        {!isCanceled && (
          <CancelModal.YesCancelButton disabled={!reason} onClick={handleClick}>
            YES, CANCEL SALE
          </CancelModal.YesCancelButton>
        )}
      </CancelModal.Container>
    </Modal>
  );
};

CancelModal.displayName = 'CancelModal';

CancelModal.Container = styled.div`
  color: ${COLORS.primaryText};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: ${(props) => (props.isCanceled ? '615px' : '1010px')};
`;

CancelModal.Title = styled.div`
  font-size: 20px;
  line-height: 23px;
  font-weight: 500;
  font-family: ${FONTS.default};
  margin-top: ${(props) => (props.isCanceled ? '7px' : '-30px')};
  margin-right: ${(props) => (props.isCanceled ? '0px' : '24px')};
  display: flex;
  justify-content: center;
  text-transform: capitalize;
  color: ${COLORS.primaryText};
`;

CancelModal.AreYouSure = styled.p`
  font-weight: 400;
  font-family: ${FONTS.default};
  font-size: 14px;
  line-height: 20px;
  margin-top: ${(props) => (props.isCanceled ? '36px' : '16px')};
  max-width: ${(props) => (props.isCanceled ? '1000px' : '417px')};
  text-align: center;
  color: ${(props) => (props.isCanceled ? COLORS.primaryText : COLORS.secondaryText)};
  padding: 0 20px;
  margin-bottom: ${(props) => (props.isCanceled ? '12px' : '35px')};
  text-transform: capitalize;
`;

CancelModal.OrderNumber = styled.span`
  font-family: ${FONTS.default};
  font-size: 14px;
  line-height: 20px;
`;

CancelModal.ProductAndReasonContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: ${(props) => (props.isCanceled ? '90%' : '100%')};
  margin-top: 44px;
  margin-bottom: 112px;
`;

CancelModal.ProductSide = styled.div`
  width: 50%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-direction: column;
`;

CancelModal.ReasonSide = styled.div`
  width: ${(props) => (props.isCanceled ? '100%' : '50%')};
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  padding-left: ${(props) => (props.isCanceled ? '0px' : '67px')};
  margin-bottom: 7px;
`;

CancelModal.Reason = styled.span`
  color: ${COLORS.secondaryText};
  text-transform: capitalize;
  font-family: ${FONTS.default};
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.5px;
`;

CancelModal.BackButton = styled.button`
  color: ${COLORS.primaryText};
  font-family: ${FONTS.emphasized};
  font-weight: 500;
  font-size: 14px;
  line-height: 24px;
  background-color: ${(props) =>
    props.isCanceled ? COLORS.primaryModalBackground : COLORS.primaryBackground};
  width: ${(props) => (props.isCanceled ? '100%' : '50%')};
  text-align: center;
  position: absolute;
  bottom: 0;
  left: 0;
  height: 80px;
  cursor: pointer;
  border-left: none;
  border-bottom: none;
  border-right: none;
  border-top: 1px solid ${COLORS.secondaryModalBorder};

  &:focus {
    outline: none;
  }

  &:disabled {
    color: ${COLORS.secondaryText};
    border: 1px solid ${COLORS.secondaryText};
    cursor: not-allowed;
  }
`;

CancelModal.YesCancelButton = styled.button`
  color: ${COLORS.primaryText};
  background-color: ${COLORS.primaryModalBackground};
  font-weight: 500;
  width: 50%;
  text-align: center;
  position: absolute;
  bottom: 0;
  border: none;
  right: 0;
  height: 80px;
  cursor: pointer;

  &:focus,
  &:hover {
    outline: none;
    background-color: ${COLORS.primaryModalBackground};
  }

  &:disabled {
    background-color: ${COLORS.primaryModalBackground};
    color: ${COLORS.secondaryTexter};
    cursor: not-allowed;
    border: 1px solid ${COLORS.primaryModalBackground};
    opacity: 0.3;
  }
`;

CancelModal.RadioChoices = styled.div`
  margin-bottom: 32px;
  margin-top: 21px;
  display: flex;
`;

CancelModal.RadioButtons = styled.div`
  max-height: 150px;
`;

CancelModal.Disclaimer = styled.p`
  font-family: ${FONTS.default};
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.5px;
  color: ${COLORS.secondaryModalBorder};
  text-transform: capitalize;
  margin-top: 8px;
  width: 388px;
`;

CancelModal.FindOrder = styled.span`
  align-items: center;
  border-bottom: 1px solid ${COLORS.secondaryText};
  color: ${COLORS.secondaryText};
  display: flex;
  font-family: ${FONTS.default};
  font-size: 12px;
  letter-spacing: 0.5px;
  line-height: 16px;
  width: 100%;
  padding-bottom: 27.5px;
  margin-bottom: 27.5px;
  justify-content: center;
  text-transform: capitalize;
  text-align: center;
`;

CancelModal.Text = styled.span`
  margin-bottom: 24px;
  font-family: ${FONTS.default};
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.5px;
  color: ${COLORS.primaryText};
  text-transform: capitalize;
`;

CancelModal.Divider = styled.div`
  background: ${COLORS.primaryBorder};
  width: 1px;
  height: 100%;
  position: absolute;
  top: 0;
  left: 529px;
`;

CancelModal.propTypes = {
  onCancel: PropTypes.func,
  modalVisible: PropTypes.bool,
  product: PropTypes.shape({
    size: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    number: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  orderNumber: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};
